import { createList } from '../../../../common/utils/graph.utils'
import { formatDateByPeriod } from '../../../../common/utils/dateFormatter'
import { AggregateWindow, FilterPoint, Point } from '../../../../features/filter/types/types'
import { EChartsSeries } from '../../../../common/components/molecule/EChartsView/EChartsView'

const PHASE_ORDER = ['L1', 'L2', 'L3', 'L4', 'N'] as const

/** Couleurs par défaut pour courbes MoMi (L1/L2/L3, multi-appareils, etc.) */
export const MOMI_SERIES_COLORS = [
  '#2563eb',
  '#16a34a',
  '#d97706',
  '#7c3aed',
  '#64748b',
  '#db2777',
  '#0d9488',
]


function readPhaseTag(p: Point): string {
  const v = p.Values as { phase?: string } | undefined
  const raw = v?.phase
  if (raw == null) return ''
  return String(raw).trim()
}

function readDeviceTag(p: Point): string {
  const v = p.Values as { device?: string } | undefined
  const raw = v?.device
  if (raw == null) return ''
  return String(raw).trim()
}

function distinctNonEmpty(values: string[]): Set<string> {
  return new Set(values.map((s) => s.trim()).filter(Boolean))
}

/**
 * Plusieurs courbes : phases CCGX (dès qu’au moins une phase est renseignée),
 * ou plusieurs appareils Schneider (≥ 2 device distincts sous le même uuid).
 */
export function momiTelemetryShouldUseMultiSeries(data: Point[] | undefined): boolean {
  if (!data?.length) return false
  if (distinctNonEmpty(data.map(readPhaseTag)).size >= 1) return true
  return distinctNonEmpty(data.map(readDeviceTag)).size >= 2
}

function sortPhaseKeys(phases: string[]): string[] {
  return [...phases].sort((a, b) => {
    const ia = PHASE_ORDER.indexOf(a as (typeof PHASE_ORDER)[number])
    const ib = PHASE_ORDER.indexOf(b as (typeof PHASE_ORDER)[number])
    if (ia !== -1 || ib !== -1) {
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    }
    if (a === '—') return 1
    if (b === '—') return -1
    return a.localeCompare(b, 'fr')
  })
}

function buildSeriesByGroupKey(
  aggWindow: AggregateWindow,
  filterPoint: FilterPoint,
  data: Point[],
  shortLabel: string,
  getKey: (p: Point) => string,
  sortKeys: (keys: string[]) => string[]
): EChartsSeries[] {
  const byKey = new Map<string, Point[]>()
  for (const p of data) {
    const k = getKey(p) || '—'
    const list = byKey.get(k) ?? []
    list.push(p)
    byKey.set(k, list)
  }

  const { listx } = createList(aggWindow, filterPoint, data)
  const xLabels = listx.map((item) => formatDateByPeriod(item, aggWindow))
  const ordered = sortKeys(Array.from(byKey.keys()))

  return ordered.map((key, idx) => {
    const pts = byKey.get(key) ?? []
    const { listValue } = createList(aggWindow, filterPoint, pts)
    return {
      name: key === '—' ? shortLabel : key,
      color: MOMI_SERIES_COLORS[idx % MOMI_SERIES_COLORS.length],
      data: xLabels.map((name, i) => ({
        name,
        value: listValue[i] ?? null,
      })),
    }
  })
}

/** Séries par phase (CCGX) ou par device (Schneider, ≥ 2 compteurs). */
export function buildMomiTelemetryMultiSeries(
  aggWindow: AggregateWindow,
  filterPoint: FilterPoint,
  data: Point[],
  shortLabel: string
): EChartsSeries[] {
  if (distinctNonEmpty(data.map(readPhaseTag)).size >= 1) {
    return buildSeriesByGroupKey(
      aggWindow,
      filterPoint,
      data,
      shortLabel,
      readPhaseTag,
      sortPhaseKeys
    )
  }
  return buildSeriesByGroupKey(
    aggWindow,
    filterPoint,
    data,
    shortLabel,
    readDeviceTag,
    (keys) =>
      [...keys].sort((a, b) => {
        if (a === '—') return 1
        if (b === '—') return -1
        return a.localeCompare(b, 'fr')
      })
  )
}
